import { useEffect, useState } from "react";
import { getDeliveryHistory } from "../api/deliveryApi";
import {
  Typography,
  Card,
  CardContent,
  Box,
  Stack,
  CircularProgress,
  Chip,
  Divider
} from "@mui/material";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    try {
      const res = await getDeliveryHistory();
      setOrders(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const glassCardStyle = {
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: "24px",
    border: "1px solid rgba(255, 255, 255, 0.6)",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
    mb: 3
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#FFFDF8" }}>
        <CircularProgress sx={{ color: "#F8C332" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e0f7fa 0%, #e8eaf6 100%)",
        pt: { xs: 10, sm: 12 }, 
        pb: { xs: 10, sm: 12 }, 
        px: { xs: 2, sm: 3 },   
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "700px" }}>
        <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", mb: 1, ml: 1 }}>
          Recent Deliveries
        </Typography>
        <Typography variant="body2" sx={{ color: "#78909c", mb: 3, ml: 1, fontWeight: "600" }}>
          Orders from the last 24 hours.
        </Typography>

        {orders.length === 0 ? (
          <Card sx={{ ...glassCardStyle, textAlign: "center", py: 6, px: 2 }} elevation={0}>
            <Typography variant="h1" mb={2}>🧾</Typography>
            <Typography variant="h6" fontWeight="800" sx={{ color: "#37474f" }}>
              No recent deliveries
            </Typography>
            <Typography variant="body2" sx={{ color: "#78909c", mt: 1 }}>
              Completed orders will appear here for 24 hours.
            </Typography>
          </Card>
        ) : (
          <Stack spacing={2}>
            {orders.map((o) => (
              <Card key={o.order_id} sx={glassCardStyle} elevation={0}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 }, "&:last-child": { pb: { xs: 2.5, sm: 3 } } }}>
                  
                  {/* HEADER */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23', lineHeight: 1.2 }}>
                        Order #{o.order_id}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#78909c', mt: 0.5, fontWeight: "600" }}>
                        {new Date(o.delivered_at).toLocaleString()}
                      </Typography>
                    </Box>
                    <Chip 
                      label="Delivered" 
                      size="small" 
                      sx={{ fontWeight: "800", bgcolor: "#DDECC8", color: "#577A34", borderRadius: "8px" }} 
                    />
                  </Box>

                  <Divider sx={{ my: 2, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />

                  {/* ITEMS */}
                  <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: '800', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                    ITEMS DELIVERED
                  </Typography>
                  <Stack spacing={1.5}>
                    {o.items.map((i, idx) => (
                      <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Typography variant="body2" fontWeight="700" sx={{ color: '#37474f', pr: 2, flex: 1, wordBreak: 'break-word' }}>
                          {i.name} <Typography component="span" fontWeight="900" sx={{ color: '#E85D3F' }}>x{i.quantity}</Typography>
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  {/* EARNING / TOTAL */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1.5, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
                    <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#3A2C23' }}>Order Value:</Typography>
                    <Typography variant="subtitle2" fontWeight="900" sx={{ color: '#2e7d32' }}>₹ {o.final_amount}</Typography>
                  </Box>

                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}